import { type DealQueueSort, type DealQueueTab } from '@carsfinders/contracts';
import { Outlet, createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';

import { Layout } from './components/layout';
import { AnalyticsPage } from './features/analytics/analytics-page';
import { LoginPage } from './features/auth/login-page';
import { RequireAdmin } from './features/auth/require-admin';
import { superadminOnly } from './features/auth/require-superadmin';
import { AuctionSchedulePage } from './features/auction-schedule/auction-schedule-page';
import { BidDetailPage } from './features/bids/bid-detail-page';
import { ClientDetailPage, parseClientTab } from './features/clients/client-detail-page';
import { ClientsPage } from './features/clients/clients-page';
import { type ClientTab } from './features/clients/components/client-detail-tabs';
import {
  DEFAULT_DEALS_SORT,
  DealsPage,
  parseDealsAuction,
  parseDealsClient,
  parseDealsSort,
  parseDealsTab,
} from './features/deals/deals-page';
import { DealerReviewPage } from './features/dealer/dealer-review-page';
import { FeedKeysPage } from './features/feed-keys/feed-keys-page';
import { FinancePage, parseFinanceView } from './features/finance/finance-page';
import { InfraCostsPage } from './features/infra-costs/infra-costs-page';
import { MarketplaceModerationPage } from './features/marketplace/marketplace-moderation-page';
import { MobileDevicesPage } from './features/mobile-devices/mobile-devices-page';
import { PricingPlansPage } from './features/pricing/pricing-plans-page';
import { DashboardPage } from './pages/dashboard';
import { DocumentsPage } from './pages/documents';
import { SettingsPage } from './pages/settings';

const rootRoute = createRootRoute({ component: Outlet });

/**
 * The only screen outside the shell: sign-in, no registration (owner decision
 * 2026-09-26). Everything else hangs off `appRoute`, which demands an admin.
 */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { next?: string; reason?: string } => ({
    next: typeof search.next === 'string' ? search.next : undefined,
    reason: typeof search.reason === 'string' ? search.reason : undefined,
  }),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: () => (
    <RequireAdmin>
      <Layout>
        <Outlet />
      </Layout>
    </RequireAdmin>
  ),
});

/*
 * The superuser-only screens (owner decision 2026-10-13). A plain admin has no
 * menu row for them and, on a typed address, gets <AccessDenied /> — api-node
 * refuses every endpoint behind them with 403 as well.
 */
const FeedKeysRoute = superadminOnly(FeedKeysPage);
const SettingsRoute = superadminOnly(SettingsPage);
const AnalyticsRoute = superadminOnly(AnalyticsPage);
const InfraCostsRoute = superadminOnly(InfraCostsPage);
const PricingRoute = superadminOnly(PricingPlansPage);
const MarketplaceRoute = superadminOnly(MarketplaceModerationPage);
// Admin-app device sessions (admin-mobile-app phase 6, SC-10).
const MobileDevicesRoute = superadminOnly(MobileDevicesPage);

const routes = [
  createRoute({ getParentRoute: () => appRoute, path: '/', component: DashboardPage }),
  createRoute({ getParentRoute: () => appRoute, path: '/clients', component: ClientsPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/clients/$id',
    component: ClientDetailPage,
    // BUG-32: the open tab survives a reload — `?tab=finances`, default omitted.
    validateSearch: (search: Record<string, unknown>): { tab?: ClientTab } => {
      const tab = parseClientTab(search.tab);
      return tab === 'verification' ? {} : { tab };
    },
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/documents', component: DocumentsPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/auction-schedule',
    component: AuctionSchedulePage,
  }),
  /*
   * «Угоди» replaced «Ставки» and «Виграні авто» (admin deals plan, phase 8).
   * The old addresses stay alive as redirects INTO the matching tab — they are
   * in bookmarks, toasts and operators' muscle memory. `/bids/$id` is NOT
   * retired: a deal row's lot links to it, and it is where a lot's bid history
   * and the relay controls for a `relayed` bid live.
   */
  createRoute({
    getParentRoute: () => appRoute,
    path: '/deals',
    component: DealsPage,
    validateSearch: (
      search: Record<string, unknown>,
    ): { tab?: DealQueueTab; sort?: DealQueueSort; client?: string; auction?: string } => {
      const tab = parseDealsTab(search.tab);
      const sort = parseDealsSort(search.sort);
      const client = parseDealsClient(search.client);
      const auction = parseDealsAuction(search.auction);
      return {
        ...(tab === 'active' ? {} : { tab }),
        ...(sort === DEFAULT_DEALS_SORT ? {} : { sort }),
        ...(client ? { client } : {}),
        ...(auction ? { auction } : {}),
      };
    },
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/bids',
    beforeLoad: () => {
      throw redirect({ to: '/deals', replace: true });
    },
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/bids/relay',
    beforeLoad: () => {
      throw redirect({ to: '/deals', search: { tab: 'relay' }, replace: true });
    },
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/won-cars',
    beforeLoad: () => {
      throw redirect({ to: '/deals', search: { tab: 'shipping' }, replace: true });
    },
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/bids/$id',
    component: BidDetailPage,
    // `?from=client` — the row on a client's «Ставки» tab opened this bid, so
    // «Назад» must return to THAT client, not to the deals list (owner,
    // 2026-10-11). Anything else reads as "came from the desk".
    validateSearch: (search: Record<string, unknown>): { from?: 'client' } =>
      search.from === 'client' ? { from: 'client' } : {},
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/dealer-applications',
    component: DealerReviewPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/marketplace',
    component: MarketplaceRoute,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/pricing', component: PricingRoute }),
  createRoute({ getParentRoute: () => appRoute, path: '/feed-keys', component: FeedKeysRoute }),
  createRoute({ getParentRoute: () => appRoute, path: '/settings', component: SettingsRoute }),
  /*
   * «Фінанси» is an ordinary route since the phase-6 cutover. It used to be
   * registered only behind a preview env flag, because every figure on it came
   * from a fixture module — an unconditional route would have let anyone reach
   * invented money by typing the URL. The fixtures are deleted and the screen
   * reads `apps/api`'s admin-finance surface, which is admin-guarded on every
   * endpoint, so the flag came out AFTER the fake money did and not before.
   */
  createRoute({
    getParentRoute: () => appRoute,
    path: '/finance',
    component: FinancePage,
    // `?view=deposits` opens «Депозити й режими» directly — the client page's
    // «Фінанси» tab links there for refunds and open top-up requests.
    validateSearch: (search: Record<string, unknown>): { view?: 'deposits' } =>
      parseFinanceView(search.view) === 'deposits' ? { view: 'deposits' } : {},
  }),
  // «Аналітика» — visitor analytics (admin-analytics plan, phase 5). Superuser
  // only (owner decision 2026-10-13); apps/api answers 403 to a plain admin too.
  createRoute({ getParentRoute: () => appRoute, path: '/analytics', component: AnalyticsRoute }),
  createRoute({ getParentRoute: () => appRoute, path: '/infra-costs', component: InfraCostsRoute }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/mobile-devices',
    component: MobileDevicesRoute,
  }),
];

const routeTree = rootRoute.addChildren([loginRoute, appRoute.addChildren(routes)]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
